import HttpStatus from 'components/Common/HttpStatus'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import Error404Page from 'components/Pages/Error404Page'
import ErrorPage from 'components/Pages/ErrorPage'
import config from 'constants/config'
import React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, Switch, withRouter } from 'react-router'
import Route from './TrackedRoute'
import loadable from 'components/utils/lazyImport'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE_SLUG,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_LAST_MINUTE_SLUG,
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
  PROPERTY_TYPE_UNIQUE_STAYS_SLUG,
} from 'constants/offer'
import { TYPE_OF_PAGE_DESTINATION, TYPE_OF_PAGE_HOLIDAY } from 'constants/landingPage'
import { TYPE_PAGE_LIMIT } from 'constants/search'
import { isCoverGeniusEnabled } from 'lib/insuranceHelpers'
import AuthRoute from './AuthRoute'
import { isLoggedIn } from 'selectors/accountSelectors'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import { FRONT_PAGE_MAP_VIEW_OFFERS_TYPES, HOTEL_PAGE_OFFER_TYPES } from 'constants/offerList'
import { RESET_PASSWORD_PATH } from 'constants/url'
import TripPlannerRoutes from 'tripPlanner/TripPlannerRoutes'
import { LUXURY_ESCAPES } from 'constants/brands'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import BusinessTravellerPageRoutes from 'businessTraveller/routes/BusinessTravellerPageRoutes'
import { buildPath } from 'lib/routeUtils'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'

const HotelSearchFullScreenPage = loadable(() => import(/* webpackChunkName: "HotelSearchFullScreenPage" */ 'components/Pages/HotelSearchPage/HotelSearchFullScreenPage'))
const HotelSearchPage = loadable(() => import(/* webpackChunkName: "HotelSearchPage" */ 'components/Pages/HotelSearchPage/HotelSearchPage'))
const PeopleLikeMePage = loadable(() => import(/* webpackChunkName: "PeopleLikeMePage" */ 'components/Pages/PeopleLikeMePage/PeopleLikeMePage'))
const InspirationPage = loadable(() => import(/* webpackChunkName: "InspirationPage" */ 'components/Pages/InspirationPage/InspirationPage'))
const HotelSearchMapPage = loadable(() => import(/* webpackChunkName: "HotelSearchMapPage" */ 'components/Pages/HotelSearchPage/HotelSearchMapPage'))
const SupportPage = loadable(() => import(/* webpackChunkName: "SupportPage" */ 'components/Pages/SupportPage'))
const VideoChatPage = loadable(() => import(/* webpackChunkName: "VideoChatPage" */ 'components/Pages/VideoChatPage'))
const AboutUsPage = loadable(() => import(/* webpackChunkName: "AboutUsPage" */ 'components/Pages/AboutUsPage'))
const CareersPage = loadable(() => import(/* webpackChunkName: "CareersPage" */ 'components/Pages/CareersPage'))
const StaticPage = loadable(() => import(/* webpackChunkName: "StaticPage" */ 'components/Pages/StaticPage'))
const BestPricePage = loadable(() => import(/* webpackChunkName: "BestPricePage" */ 'components/Pages/BestPricePage'))
const FlightsLandingPage = loadable(() => import(/* webpackChunkName: "FlightsLandingPage" */ 'components/Pages/PromotionalPages/FlightsLandingPage'))
const FlightsSearchLandingPage = loadable(() => import(/* webpackChunkName: "FlightsSearchLandingPage" */ 'components/Pages/PromotionalPages/FlightsLandingPage/FlightsSearchLandingPage'))
const FlightsSearchPage = loadable(() => import(/* webpackChunkName: "FlightsSearchPage" */ 'components/Flights/FlightsSearchPage'))
const FlightsSearchResultsPage = loadable(() => import(/* webpackChunkName: "FlightsSearchResultsPage" */ 'components/Flights/FlightsSearchResultsPage'))
const LegalPages = loadable(() => import(/* webpackChunkName: "LegalPages" */ 'components/Pages/LegalPages'))
const ReferPage = loadable(() => import(/* webpackChunkName: "ReferPage" */ 'marketing/pages/Refer'))
const NewsPage = loadable(() => import(/* webpackChunkName: "NewsPage" */ 'components/Pages/NewsPage'))
const FrequentFlyerPage = loadable(() => import(/* webpackChunkName: "FrequentFlyerPage" */ 'components/Pages/FrequentFlyerPage'))
const MyEscapesPage = loadable(() => import(/* webpackChunkName: "MyEscapesPage" */ 'components/Pages/MyEscapesPage'))
const UserReviews = loadable(() => import(/* webpackChunkName: "UserReviews" */ 'components/Pages/UserReviews'))
const OrderPage = loadable(() => import(/* webpackChunkName: "OrderPage" */ 'components/Pages/MyEscapesPage/OrderPage'))
const MySubscriptionsPage = loadable(() => import(/* webpackChunkName: "MySubscriptionsPage" */ 'components/Pages/MySubscriptionsPage'))
const MyWLSubscriptionsPage = loadable(() => import(/* webpackChunkName: "MySubscriptionsPage" */ 'components/Pages/MyWLSubscriptionsPage'))
const AccountDetailsPage = loadable(() => import(/* webpackChunkName: "AccountDetailsPage" */ 'components/Pages/AccountDetailsPage'))
const AccountDetailsTravellerPage = loadable(() => import(/* webpackChunkName: "AccountDetailsTravellerPage" */ 'components/Pages/AccountDetailsTravellerPage'))
const CreditsAndPaymentPage = loadable(() => import(/* webpackChunkName: "CreditsPage" */ 'components/Pages/CreditsPage/CreditsAndPaymentPage'))
const BookingPage = loadable(() => import(/* webpackChunkName: "BookingPage" */ 'components/Pages/Booking'))
const BookingDeepLink = loadable(() => import(/* webpackChunkName: "BookingDeepLink" */ 'components/Pages/BookingDeepLink'))
const TourTravellerInfoPage = loadable(() => import(/* webpackChunkName: "TourTravellerInfoPage" */ 'components/Pages/TourTravellerInfo/TourTravellerInfoPage'))
const GiftCardCheckout = loadable(() => import(/* webpackChunkName: "GiftCardCheckout" */ 'components/Pages/GiftCardsPage/Checkout'))
const GiftCardConfirmationPage = loadable(() => import(/* webpackChunkName: "GiftCardConfirmationPage" */ 'components/Pages/GiftCardsPage/Confirmation'))
const GiftCardViewPage = loadable(() => import(/* webpackChunkName: "GiftCardViewPage" */ 'components/Pages/GiftCardsPage/View'))
const MapViewPage = loadable(() => import(/* webpackChunkName: "MapViewPage" */ 'components/Pages/MapViewPage'))
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ 'components/Pages/LandingPage/LandingPage'))
const LandingPageLere = loadable(() => import(/* webpackChunkName: "LandingPageLere" */ 'components/Pages/LandingPage/LandingPageLere'))
const TravelInsurancePage = loadable(() => import(/* webpackChunkName: "TravelInsurancePage" */ 'components/Pages/Insurance/LandingPage'))
const TravelInsuranceCoverGeniusPage = loadable(() => import(/* webpackChunkName: "TravelInsuranceCoverGeniusPage" */ 'components/Pages/Insurance/LandingPage/CoverGenius'))
const TravelInsuranceQuote = loadable(() => import(/* webpackChunkName: "TravelInsuranceQuotePage" */ 'components/Pages/Insurance/QuotePage'))
const TravelInsuranceAddCheckout = loadable(() => import(/* webpackChunkName: "TravelInsuranceAddCheckout" */ 'components/Pages/Insurance/AddCheckoutPage'))
const TravelInsuranceDetails = loadable(() => import(/* webpackChunkName: "TravelInsuranceDetailsPage" */ 'components/Pages/Insurance/DetailsPage'))
const TravelInsurancePayment = loadable(() => import(/* webpackChunkName: "TravelInsurancePayment" */ 'components/Pages/Insurance/PaymentPage'))
const EscapesFinderPage = loadable(() => import(/* webpackChunkName: "EscapesFinderPage" */ 'components/Pages/EscapesFinderPage'))
const ConfirmationPage = loadable(() => import(/* webpackChunkName: "ConfirmationPage" */ 'components/Booking/Confirmation'))
const CreditRebookingConfirmationPage = loadable(() => import(/* webpackChunkName: "CreditRebookingConfirmationPage" */ 'components/Flights/CreditRebooking/Confirmation/CreditRebookingConfirmation/ConfirmationPageContainer'))
const HolidayLabPage = loadable(() => import(/* webpackChunkName: "HolidayLabPage" */ 'components/Pages/HolidayLab/HolidayLabPage'))
const BNBLLandingPage = loadable(() => import(/* webpackChunkName: "BNBLLandingPage" */ 'components/Pages/BNBLLandingPage'))
const TWCLandingPage = loadable(() => import(/* webpackChunkName: "TourLandingPage" */ 'components/Pages/TWCLandingPage'))
const EWCLandingPage = loadable(() => import(/* webpackChunkName: "EscapeLandingPage" */ 'components/Pages/EWCLandingPage'))
const ContentHub = loadable(() => import(/* webpackChunkName: "ContentHub" */ 'components/Pages/LandingPage/Hub'))
const OfferPage = loadable(() => import(/* webpackChunkName: "OfferPage" */ 'components/OfferPage'))
const VillasOfferPage = loadable(() => import(/* webpackChunkName: "VillasOfferPage" */ 'components/Pages/Villas/OfferPage/VillasOfferPage'))
const BedBankOfferPage = loadable(() => import(/* webpackChunkName: "OfferPage" */ 'components/Pages/BedbankOfferPage'))
const PaginatedOffersPage = loadable(() => import(/* webpackChunkName: "PaginatedOfferPage" */ 'components/Pages/PaginatedOffersPage'))
const TourV2OfferPage = loadable(() => import(/* webpackChunkName: "TourV2OfferPage" */ 'components/Pages/TourV2/OfferPage/TourV2OfferPage'))
const TourV2OfferVariationPage = loadable(() => import(/* webpackChunkName: "TourV2OfferVariationPage" */ 'components/Pages/TourV2/OfferPage/TourV2OfferVariationPage'))
const TourV2SearchPage = loadable(() => import(/* webpackChunkName: "TourV2SearchPage" */ 'components/Pages/TourV2/SearchPage/TourV2SearchPage'))
const HotelsPage = loadable(() => import(/* webpackChunkName: "HotelsPage" */ 'components/Pages/TypesPage/HotelsPage'))
const UniqueStaysPage = loadable(() => import(/* webpackChunkName: "UniqueStaysPage" */ 'components/Pages/TypesPage/UniqueStaysPage'))
const ToursPage = loadable(() => import(/* webpackChunkName: "ToursPage" */ 'components/Pages/TypesPage/ToursPage'))
const UltraLuxVerticalPage = loadable(() => import(/* webpackChunkName: "UltraLuxVerticalPage" */ 'components/Pages/TypesPage/UltraLux/UltraLuxVerticalPage'))
const LuxBundlesPage = loadable(() => import(/* webpackChunkName: "LuxBundlesVerticalPage" */ 'components/Pages/TypesPage/LuxBundlesPage'))
const DestinationProductPage = loadable(() => import(/* webpackChunkName: "DestinationProductPage" */ 'components/Pages/DestinationProductPage/DestinationProductPage'))
const FrontPageUser = loadable(() => import(/* webpackChunkName: "FrontPageUser" */'components/Pages/FrontPage/FrontPageUser'))
const FrontPageGuest = loadable(() => import(/* webpackChunkName: "FrontPageGuest" */'components/Pages/FrontPage/FrontPageGuest'))
const DestinationAlerts = loadable(() => import(/* webpackChunkName: "DestinationAlerts" */ 'components/Pages/DestinationAlerts/LandingPage'))
const DestinationPage = loadable(() => import(/* webpackChunkName: "DestinationPage" */ 'components/Pages/DestinationPage/DestinationPage'))
const PopularDestinationPage = loadable(() => import(/* webpackChunkName: "PopularDestinationPage" */ 'components/Pages/DestinationPage/PopularDestinationPage'))
const VFFLandingPage = loadable(() => import(/* webpackChunkName: "VFFLandingPage" */ 'components/Pages/PromotionalPages/VFFLandingPage'))
const KrisFlyerLandingPage = loadable(() => import(/* webpackChunkName: "KrisFlyerLandingPage" */ 'components/Pages/PromotionalPages/KrisFlyerLandingPage'))
const ClubVistaraLandingPage = loadable(() => import(/* webpackChunkName: "ClubVistaraLandingPage" */ 'components/Pages/PromotionalPages/ClubVistaraLandingPage'))
const MasterCardLandingPage = loadable(() => import(/* webpackChunkName: "MasterCardPage" */ 'components/Pages/PromotionalPages/MasterCard'))
const GiftCardsPageContainer = loadable(() => import(/* webpackChunkName: "GiftCardsPage" */ 'components/Pages/GiftCardsPage'))
const LatitudeLandingPage = loadable(() => import(/* webpackChunkName: "LatitudeLandingPage" */ 'components/Pages/PromotionalPages/LatitudeLandingPage'))
const KlarnaLandingPage = loadable(() => import(/* webpackChunkName: "KlarnaLandingPage" */ 'components/Pages/PromotionalPages/KlarnaLandingPage'))
const AfterPayLandingPage = loadable(() => import(/* webpackChunkName: "AfterPayLandingPage" */ 'components/Pages/PromotionalPages/AfterPayLandingPage'))
const HoolahLandingPage = loadable(() => import(/* webpackChunkName: "HoolahLandingPage" */ 'components/Pages/PromotionalPages/HoolahLandingPage'))
const RedirectToShopbackPage = loadable(() => import(/* webpackChunkName: "RedirectToShopbackPage" */ 'components/Pages/PromotionalPages/RedirectToShopbackPage'))
const AtomeLandingPage = loadable(() => import(/* webpackChunkName: "AtomeLandingPage" */ 'components/Pages/PromotionalPages/AtomeLandingPage'))
const WaaveLandingPage = loadable(() => import(/* webpackChunkName: "WaaveLandingPage" */ 'components/Pages/PromotionalPages/WaaveLandingPage'))
const LastMinuteEscapesPage = loadable(() => import(/* webpackChunkName: "LastMinuteEscapesPage" */ 'components/Pages/TypesPage//LastMinuteEscapes/LastMinuteEscapesPage'))
const AccountPage = loadable(() => import(/* webpackChunkName: "AccountLandingPage" */ 'components/Pages/AccountPage/AccountLandingPage'))
const ExperienceOfferPage = loadable(() => import(/* webpackChunkName: "ExperienceOfferPage" */ 'components/Experiences/ExperienceOfferPage/ExperienceOfferPage'))
const ExperiencesPage = loadable(() => import(/* webpackChunkName: "ExperiencesPage" */ 'components/Experiences/ExperiencesPage/ExperiencesPage'))
const ExperienceSearchMapPage = loadable(() => import(/* webpackChunkName: "ExperienceSearchMapPage" */ 'components/Experiences/ExperiencesSearch/ExperienceSearchMapPage'))
const CruisesPage = loadable(() => import(/* webpackChunkName: "CruisesPage" */ 'components/Cruises/CruisesPage/CruisesPage'))
const CruiseSearchPage = loadable(() => import(/* webpackChunkName: "CruiseSearchPage" */ 'components/Cruises/SearchPage/CruiseSearchPage'))
const CruiseOfferPage = loadable(() => import(/* webpackChunkName: "CruiseOfferPage" */ 'components/Cruises/CruiseOfferPage/CruiseOfferPage'))
const CruiseLineLandingPage = loadable(() => import(/* webpackChunkName: "CruiseLineLandingPage" */ 'components/Cruises/CruiseLineLandingPage/CruiseLineLandingPage'))
const ExperienceSearchPage = loadable(() => import(/* webpackChunkName: "ExperienceSearchPage" */ 'components/Experiences/ExperiencesSearch/ExperienceSearchPage'))
const DestinationGuidePage = loadable(() => import(/* webpackChunkName: "DestinationGuidePage" */ '../../../../content/pages/DestinationGuidePage'))
const TopPicksForMePage = loadable(() => import(/* webpackChunkName: "TopPicksForMePage" */ 'components/Recommendations/TopPicksForMe/TopPicksForMePage'))
const RefereeLandingPage = loadable(() => import(/* webpackChunkName: "RefereeLandingPage" */ '../../../../content/pages/RefereeLanding'))
const GoogleHotelAdPage = loadable(() => import(/* webpackChunkName: "GoogleHotelAdPage" */ '../../../../marketing/pages/GoogleHotelAdPage'))
const CheckoutPageRoutes = loadable(() => import(/* webpackChunkName: "CheckoutPageRoutes" */ 'checkout/routes'))
const CheckoutDeepLinkPage = loadable(() => import(/* webpackChunkName: "CheckoutDeepLinkPage" */ 'checkout/Pages/DeepLinkPage'))
const CheckoutGatewayPage = loadable(() => import(/* webpackChunkName: "CheckoutGatewayPage" */ 'checkout/Pages/GatewayPage'))
const AncillaryPaymentPage = loadable(() => import(/* webpackChunkName: "AncillaryPaymentPage" */ 'checkout/Pages/AncillaryPaymentPage'))

const ExperienceVoucher = loadable(() => import(/* webpackChunkName: "ExperienceVoucher" */ 'components/Pages/ExperienceVoucher'))
const PropertyPage = loadable(() => import(/* webpackChunkName: "PropertyPage" */ '../../../../marketing/pages/PropertyPage'))
const SplashPage = loadable(() => import(/* webpackChunkName: "SplashPage" */ 'components/Pages/SplashPage'))
const RedeemGifted = loadable(() => import(/* webpackChunkName: "RedeemGifted" */ 'components/Pages/RedeemGifted'))
const AuCompetitionsPage = loadable(() => import(/* webpackChunkName: "AuCompetitionsPage" */ '../../../../marketing/pages/AuCompetitionPage'))
const IntlCompetitionsPage = loadable(() => import(/* webpackChunkName: "IntlCompetitionsPage" */ '../../../../marketing/pages/INTLCompetitionPage'))
const LETVS6CompetitionPage = loadable(() => import(/* webpackChunkName: "CompetitionPage" */ '../../../../marketing/pages/LETVS6CompetitionPage'))
const AffiliateLandingPage = loadable(() => import(/* webpackChunkName: "AffiliateLandingPage" */ '../../../../marketing/pages/AffiliateLandingPage'))
const HolidayTypeLandingPage = loadable(() => import(/* webpackChunkName: "HolidayTypeLandingPage" */ 'components/Pages/HolidayTypeLandingPage'))
const CarHirePage = loadable(() => import(/* webpackChunkName: "CarHirePage" */ 'components/CarHire/CarHirePage/CarHirePage'))
const CarHireSearchPage = loadable(() => import(/* webpackChunkName: "CarHireSearchPage" */ 'components/CarHire/CarHireSearchPage/CarHireSearchPage'))
const NewHomePage = loadable(() => import(/* webpackChunkName: "NewHomePage" */ '../../../../home/pages/HomePage'))
const NewDestinationLandingPage = loadable(() => import(/* webpackChunkName: "NewDestinationLandingPage" */ '../../../../home/pages/DestinationLandingPage'))
const LETVLandingPage = loadable(() => import(/* webpackChunkName: "LETVLandingpage" */'../../../../marketing/pages/LETVLandingPage'))
const LETVEpisodePage = loadable(() => import(/* webpackChunkName: "LETVEpisodePage" */'../../../../marketing/pages/LETVEpisodePage'))

const promotionalPages = {
  VFFLandingPage,
  KrisFlyerLandingPage,
  ClubVistaraLandingPage,
  MasterCardLandingPage,
  GiftCardsPageContainer,
  LatitudeLandingPage,
  KlarnaLandingPage,
  AfterPayLandingPage,
  HoolahLandingPage,
  RedirectToShopbackPage,
  AtomeLandingPage,
  WaaveLandingPage,
}

const onlyTour: App.LEContractedOfferType[] = [OFFER_TYPE_TOUR]
const onlyLastMinute: App.LEContractedOfferType[] = [OFFER_TYPE_LAST_MINUTE]
const onlyHotel: App.LEContractedOfferType[] = [OFFER_TYPE_HOTEL]
const mapPageFilters: App.OfferListFilters = {
  limit: TYPE_PAGE_LIMIT,
}
const ultraLuxOfferTypes: App.LEContractedOfferType[] = [OFFER_TYPE_HOTEL, 'tactical_ao_hotel']
const ultraLuxMapPageFilters: App.OfferListFilters = {
  ...mapPageFilters,
  propertySubCategories: ['Ultra Lux'],
}

interface Props extends RouteComponentProps<any> {
  isLoggedIn: boolean
  currentRegionCode: string
  standaloneFlightsEnabled: boolean
  standaloneCruiseEnabled: boolean
  scooponHomeEnabled: boolean
}

function PageRoutes(props: Props) {
  const { match, isLoggedIn, currentRegionCode, standaloneFlightsEnabled, standaloneCruiseEnabled } = props
  const newHomePageEnabled = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.isNewHomePageTestEnabled)
  const newAuHomePageEnabled = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.isAuNewHomePageTestEnabled)

  return (
    <Switch>
      <RedirectWithStatus code={301} from={buildPath(match, '/offer/855b9dc3-e1ab-4069-9566-12712772eded')} to={buildPath(match, '/partner/fiji-marriott-resort-momi-bay/855b9dc3-e1ab-4069-9566-12712772eded')} />
      {/* Offer list Pages */}
      <Route exact path={buildPath(match, `/${OFFER_TYPE_TOUR_SLUG}`)} component={ToursPage} />
      <Route path={buildPath(match, `/${OFFER_TYPE_TOUR_SLUG}/map-view`)}>
        <MapViewPage
          listViewPath={`/${OFFER_TYPE_TOUR_SLUG}`}
          offerTypes={onlyTour}
        />
      </Route>
      <Route exact path={buildPath(match, `/${OFFER_TYPE_HOTEL_SLUG}`)} component={HotelsPage} />
      <Route path={buildPath(match, `/${OFFER_TYPE_HOTEL_SLUG}/map-view`)}>
        <MapViewPage listViewPath={`/${OFFER_TYPE_HOTEL_SLUG}`}
          offerTypes={onlyHotel}
          filters={mapPageFilters}
        />
      </Route>
      <Route exact path={buildPath(match, `/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`)} component={LuxBundlesPage} />
      {config.ULTRALUX_ENABLED && <Route exact path={buildPath(match, `/${PRODUCT_TYPE_ULTRALUX_SLUG}`)} component={UltraLuxVerticalPage} />}
      {config.ULTRALUX_ENABLED && <Route path={buildPath(match, `/${PRODUCT_TYPE_ULTRALUX_SLUG}/map-view`)}>
        <MapViewPage
          listViewPath={`/${PRODUCT_TYPE_ULTRALUX_SLUG}`}
          offerTypes={ultraLuxOfferTypes}
          filters={ultraLuxMapPageFilters}
          heading={<>Explore our <i>ultra lux</i> offers</>}
        />
      </Route>}
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/experiences')} component={ExperiencesPage} />}
      {standaloneCruiseEnabled && <Route exact path={buildPath(match, '/cruises')} component={CruisesPage} />}
      {config.CRUISES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/cruises/:offerId')} component={CruiseOfferPage} />}
      {config.CRUISES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/cruises/:offerSlug/:offerId')} component={CruiseOfferPage} />}
      {config.CRUISES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/cruise-line/:cruiseLineSlug')} component={CruiseLineLandingPage} />}

      {/* Car Hire Pages */}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && <Route exact path={buildPath(match, '/car-hire')} component={CarHirePage} />}

      {/* Offer pages */}
      <Route exact path={buildPath(match, `/${PROPERTY_TYPE_UNIQUE_STAYS_SLUG}`)} component={UniqueStaysPage} />
      <Route path={buildPath(match, `/${PROPERTY_TYPE_UNIQUE_STAYS_SLUG}/map-view`)}>
        <MapViewPage
          listViewPath={`/${PROPERTY_TYPE_UNIQUE_STAYS_SLUG}`}
          offerTypes={HOTEL_PAGE_OFFER_TYPES(currentRegionCode)}
          filters={mapPageFilters}
        />
      </Route>
      <Route exact path={buildPath(match, '/offer/:offerId')}>
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </Route>
      <Route path={buildPath(match, '/offer/:offerSlug/:offerId')}>
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </Route>
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/experience/:id')}>
        {({ match }) => <ExperienceOfferPage key={match?.params.offerId} />}
      </Route>}
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route path={buildPath(match, '/experience/:slug/:id')}>
        {({ match }) => <ExperienceOfferPage key={match?.params.offerId} />}
      </Route>}
      <Route path={buildPath(match, '/partner/:offerSlug/:offerId')}>
        {({ match }) => <BedBankOfferPage key={match?.params.offerId} />}
      </Route>
      <Route exact path={buildPath(match, `/${OFFER_TYPE_LAST_MINUTE_SLUG}`)} component={LastMinuteEscapesPage} />
      <Route path={buildPath(match, `/${OFFER_TYPE_LAST_MINUTE_SLUG}/map-view`)}>
        <MapViewPage
          listViewPath={`/${OFFER_TYPE_LAST_MINUTE_SLUG}`}
          offerTypes={onlyLastMinute}
          filters={mapPageFilters}
        />
      </Route>
      <Route path={buildPath(match, '/page/:pageNum')} component={PaginatedOffersPage} />
      <Route path={buildPath(match, '/map-view')}>
        <MapViewPage
          offerTypes={FRONT_PAGE_MAP_VIEW_OFFERS_TYPES(currentRegionCode)} listViewPath="/"
          filters={mapPageFilters}
        />
      </Route>
      // If brand defines a particular default homepage, redirect "/" to that location
      {
        config.defaultHomePage && (
          <Route
            exact
            path={buildPath(match, '/')}
            component={({ location }) => (
              <RedirectWithStatus
                code={302}
                to={location.pathname.replace(/$/, config.defaultHomePage)}
              />
            )
            }
          />
        )
      }

      {(!newHomePageEnabled && !newAuHomePageEnabled) && (
        <Route exact path={buildPath(match, '/')}>
          {isLoggedIn && <FrontPageUser />}
          {!isLoggedIn && <FrontPageGuest />}
        </Route>
      )}

      {(newHomePageEnabled || newAuHomePageEnabled) && <Route exact path={buildPath(match, '/')} component={NewHomePage} /> }
      <RedirectWithStatus code={301} from={buildPath(match, '/villas')} to={buildPath(match, '/holidays/homes-and-villas')} />

      {/* Support Pages */}
      {config.SUPPORT_ENABLED && <Route path={buildPath(match, '/support')} component={SupportPage} key={Math.random()} />}
      {/* See https://github.com/lux-group/www-le-customer/pull/4838#discussion_r597384790 for explanation of this key */}

      {/* Sales Team Contact Page */}
      <Route path={buildPath(match, '/request-video-chat/:offerId/:packageId')} component={VideoChatPage} />
      {config.ENABLE_LETV_S6_SPIN_COMPETITION_PAGE && <Route path={buildPath(match, '/tv/season-6/competition')} component={LETVS6CompetitionPage} />}

      {/* Search Pages */}
      <Route path={buildPath(match, '/search-fullscreen')} component={HotelSearchFullScreenPage} />
      <Route exact path={buildPath(match, '/search')} component={HotelSearchPage} />
      <Route exact path={buildPath(match, '/people-like-me')} component={PeopleLikeMePage} />
      <Route exact path={buildPath(match, '/people-like-me/inspiration')} component={InspirationPage} />
      <Route exact path={buildPath(match, '/search/map')} component={HotelSearchMapPage} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/search/experiences')} component={ExperienceSearchPage} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/search/experiences/map')} component={ExperienceSearchMapPage} />}
      {config.CRUISES_STANDLONE_ENABLED && <Route exact path={buildPath(match, '/search/cruises')} component={CruiseSearchPage} />}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && <Route exact path={buildPath(match, '/search/car-hire')} component={CarHireSearchPage} />}

      <RedirectWithStatus code={301} from={buildPath(match, '/amex')} to={buildPath(match, '/')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/refund-policy')} to={buildPath(match, '/booking-policies')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/frequently-asked-questions')} to={buildPath(match, '/support')} />

      {/* Account pages */}
      <AuthRoute exact path={buildPath(match, '/account')} component={AccountPage} />
      <AuthRoute exact path={buildPath(match, '/account/credits-payments')} component={CreditsAndPaymentPage} />
      <AuthRoute showPopup path={buildPath(match, '/account/my-escapes/experiences/:experienceItemId/voucher')} component={ExperienceVoucher} />
      <AuthRoute showPopup exact path={buildPath(match, '/account/my-escapes/order/:orderId')} component={OrderPage} />
      {/* Apps are still pointing to /my-escapes, so redirect to the updated route */}
      <RedirectWithStatus code={301} from={buildPath(match, '/my-escapes/:modalType?')} to={buildPath(match, '/account/my-escapes/:modalType?')} />
      <AuthRoute showPopup path={buildPath(match, '/account/my-escapes/:tab(upcoming|cancelled|past)?')} component={MyEscapesPage} />
      <AuthRoute exact path={buildPath(match, '/account/details/traveller')} component={AccountDetailsTravellerPage} />
      <AuthRoute exact path={buildPath(match, '/account/details')} component={AccountDetailsPage} />
      <AuthRoute path={buildPath(match, '/booking-form/:orderId?')} component={TourTravellerInfoPage} />
      <AuthRoute showPopup exact path={buildPath(match, '/review/:orderId?')} component={UserReviews} />
      <Route exact path={buildPath(match, `/${RESET_PASSWORD_PATH}`)} />

      {/* Subscriptions Pages */}
      <AuthRoute path={buildPath(match, '/account/my-subscriptions')} component={config.BRAND === LUXURY_ESCAPES ? MySubscriptionsPage : MyWLSubscriptionsPage} />
      <RedirectWithStatus code={301} from={buildPath(match, '/account/subscriptions')} to={buildPath(match, '/account/my-subscriptions')} />

      {config.DESTINATION_ALERTS_ENABLED && <AuthRoute showPopup path={buildPath(match, '/account/my-destinations')} component={DestinationAlerts} />}
      <RedirectWithStatus code={301} exact from={buildPath(match, '/account/payment-cards')} to={buildPath(match, '/account/credits-payments')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/my-subscriptions')} to={buildPath(match, '/account/my-subscriptions')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/account-details')} to={buildPath(match, '/account/details')} />

      {/* Insurance pages */}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path={buildPath(match, '/insurance/quote')} component={TravelInsuranceQuote} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path={buildPath(match, '/insurance/add')} component={TravelInsuranceAddCheckout} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path={buildPath(match, '/insurance/details')} component={TravelInsuranceDetails} />}
      {isCoverGeniusEnabled(currentRegionCode) && <AuthRoute showPopup path={buildPath(match, '/insurance/payment')} component={TravelInsurancePayment} />}
      <RedirectWithStatus code={301} from={buildPath(match, '/insurance/checkout')} to={buildPath(match, '/insurance/quote')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/insurance/confirmation')} to={buildPath(match, '/booking/confirmation')} />

      {/* Gift card pages */}
      <AuthRoute path={buildPath(match, '/gift-cards/checkout')} component={GiftCardCheckout} />
      <AuthRoute path={buildPath(match, '/gift-cards/confirmation')} component={GiftCardConfirmationPage} />
      <AuthRoute showPopup path={buildPath(match, '/gift-card/:code')} component={GiftCardViewPage} />

      {/* Booking pages */}
      <AuthRoute showPopup path={buildPath(match, '/booking/confirmation')} component={ConfirmationPage} />
      <AuthRoute showPopup path={buildPath(match, '/booking/flights-credit-confirmation')} component={CreditRebookingConfirmationPage} />
      <AuthRoute showPopup path={buildPath(match, '/booking/:step')} component={BookingPage} />
      <AuthRoute showPopup path={buildPath(match, '/booking-deep-link')} component={BookingDeepLink} />
      <AuthRoute showPopup path={buildPath(match, '/bb/booking/confirmation')} component={ConfirmationPage} />
      <AuthRoute showPopup path={buildPath(match, '/bb/booking/:step')} component={BookingPage} />
      <RedirectWithStatus code={301} from={buildPath(match, '/confirmation/:orderId')} to={buildPath(match, '/booking/confirmation?orderId=:orderId')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/bb/booking/confirmation')} to={buildPath(match, '/booking/confirmation')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/bb/booking/:step')} to={buildPath(match, '/booking/:step')} />

      {/* Redeem gift hotel and experience */}
      <Route exact path={buildPath(match, '/redeem/:redeemCode/:orderId/:offerId')} component={RedeemGifted} />

      {/* Villas Pages */}
      {config.ENABLE_VILLA_RENTALS && <Route exact path={buildPath(match, '/villa/:slug/:offerId')} component={VillasOfferPage} />}

      {/* TourV2Pages */}
      <Route exact path={buildPath(match, '/tour/:offerId')}>
        {({ match }) => <TourV2OfferPage key={match?.params.offerId} />}
      </Route>

      <Route exact path={buildPath(match, '/tour/:offerId/:variationId')} component={TourV2OfferVariationPage} />

      {config.TOUR_V2_SEARCH_ENABLED && <Route exact path={buildPath(match, '/search/tours')} component={TourV2SearchPage} />}

      {config.hasReferralProgram && (
        <Route exact path={buildPath(match, '/refer')} component={ReferPage} />
      )}
      {config.hasReferralProgram && (
        <Route path={buildPath(match, '/refer/:referralCode')} component={RefereeLandingPage} />
      )}

      <Route path={buildPath(match, '/google-hotel-ads')} component={GoogleHotelAdPage} />
      {config.ENABLE_ONE_PROPERTY_PAGE && <Route path={buildPath(match, '/property/:propertyNameAndId')} component={PropertyPage} />}
      <Route exact path={buildPath(match, '/tv/season-6/:episode')} component={LETVEpisodePage} />
      <Route exact path={buildPath(match, '/tv/season-6')} component={LETVLandingPage} />
      {config.ENABLE_AU_COMPETITION && <Route path={buildPath(match, '/hunter-valley-competition')} component={AuCompetitionsPage} />}
      {config.ENABLE_INTL_COMPETITION && <Route path={buildPath(match, '/spin-the-wheel-competition')} component={IntlCompetitionsPage} />}
      <Route path={buildPath(match, '/affiliate-program')} component={AffiliateLandingPage} />
      <RedirectWithStatus code={301} from={buildPath(match, '/letv-landing-page.p1')} to={buildPath(match, '/letvcompetition')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/citi')} to={buildPath(match, '/save-with-citi.p1')} />

      <Route path={buildPath(match, '/sharethis/verify')} component={() => {
        const mainUrl = `https://platform-api.sharethis.com/js/sharethis.js#property=${config.GOOGLE_APP_ID}`
        const script = document.createElement('script')
        script.setAttribute('id', 'sharethis-js')
        script.src = mainUrl
        script.async = true
        document.body.appendChild(script)
        return null
      }} />

      {/* Content hub pages */}
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <RedirectWithStatus code={301} from={buildPath(match, '/pages/:slug')} to={buildPath(match, '/:slug.p1')} />
      }
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <Route exact path={buildPath(match, '/:slug.p1')} component={ContentHub} />
      }

      {config.staticPages.map(sp => (
        sp.url?.to && <Route
          exact
          key={sp.url.to}
          path={buildPath(match, sp.url.to)}
          render={props => {
            if (sp.type === 'bestPrice') {
              return <BestPricePage />
            } else if (sp.type === 'aboutUs') {
              return <AboutUsPage />
            } else if (sp.type === 'careersAndCulture') {
              return <CareersPage />
            } else if (sp.type === 'termsAndConditions') {
              return <LegalPages slug="terms-and-conditions" location={props.location} />
            } else if (sp.type === 'refundPolicy') {
              return <LegalPages slug="refund-policy" location={props.location} />
            } else if (sp.type === 'news') {
              return <NewsPage slug="news" location={props.location} />
            } else if (sp.type === 'privacyPolicy') {
              return <LegalPages slug="privacy-policy" location={props.location} />
            } else if (sp.type === 'giftcardTermsAndConditions') {
              return <LegalPages slug="giftcards-terms" location={props.location} />
            } else if (sp.type === 'flights') {
              return <LegalPages slug="flights-policies" location={props.location} />
            } else if (sp.type === 'frequentFlyerPrograms') {
              return <FrequentFlyerPage slug="frequent-flyer-programs" location={props.location} />
            } else if (sp.type === 'specialProductsPromotions') {
              return <LegalPages slug="promotions" location={props.location} />
            } else if (sp.type === 'bookingPolicies') {
              return <LegalPages slug="booking-policies" location={props.location} />
            } else if (sp.type === 'bestPriceTerms') {
              return <LegalPages slug="best-price-guarantee-terms" location={props.location} />
            } else if (sp.type === 'feeSchedule') {
              return <LegalPages slug="fee-schedule" location={props.location} />
            } else if (sp.type === 'travelInsurance') {
              return <TravelInsurancePage />
            } else if (sp.type === 'travelInsuranceCoverGenius') {
              return <TravelInsuranceCoverGeniusPage />
            } else if (sp.type === 'bnblLanding') {
              return <BNBLLandingPage />
            } else if (sp.type === 'twcLanding') {
              return <TWCLandingPage />
            } else if (sp.type === 'ewcLanding') {
              return <EWCLandingPage />
            } else {
              return <StaticPage type={sp.type} title={sp.text} />
            }
          }}
        />
      ))}

      {/* Promotional Pages */}
      <RedirectWithStatus from={buildPath(match, '/qantasfrequentflyer')} to="/au/qantas-frequent-flyer" />
      {!standaloneFlightsEnabled && <Route path={buildPath(match, '/flights')} component={FlightsLandingPage} />}
      {standaloneFlightsEnabled && <Route exact path={buildPath(match, '/flights')} component={FlightsSearchLandingPage} />}
      {standaloneFlightsEnabled && <Route exact path={buildPath(match, '/flights/:dealCategory')} component={FlightsSearchLandingPage} />}
      {standaloneFlightsEnabled && <Route path={buildPath(match, '/flights-search')} component={FlightsSearchPage} />}
      {standaloneFlightsEnabled && <Route path={buildPath(match, '/flights-search-results')} component={FlightsSearchResultsPage} />}
      <Route path={buildPath(match, '/escapes-finder')} component={EscapesFinderPage} />
      <Route path={buildPath(match, '/holidaylab')} component={HolidayLabPage} />
      <RedirectWithStatus from={buildPath(match, '/kris-flyer')} to={buildPath(match, '/krisflyer')} />
      {config.promotionalPages.map(pp => {
        const expired = pp.expiresAt && Date.parse(pp.expiresAt) < Date.now()
        const PromotionalComponent = promotionalPages[pp.component]
        return <Route
          key={pp.url}
          path={buildPath(match, pp.url)}
        >
          {expired && <RedirectWithStatus code={301} to={match.url} />}
          {!expired && <PromotionalComponent />}
        </Route>
      })}
      {config.holidayPages &&
        <RedirectWithStatus code={301} from={buildPath(match, '/holidays/:slug')} to={buildPath(match, '/:slug.h1')} />
      }
      {config.holidayPages &&
        <Route path={buildPath(match, '/:slug.h1')} component={(props) => <LandingPage {...props} typeOfPage={TYPE_OF_PAGE_HOLIDAY} />} />
      }
      {config.destinationPages &&
        <RedirectWithStatus code={301} from={buildPath(match, '/destinations/:slug')} to={buildPath(match, '/:slug.d1')} />
      }
      {config.destinationPages &&
        <Route path={buildPath(match, '/:slug.d1')} component={(props) => <LandingPage {...props} typeOfPage={TYPE_OF_PAGE_DESTINATION} />} />
      }
      {config.destinationPages &&
        <Route path={buildPath(match, '/:slug.d1.lere')} component={(props) => <LandingPageLere {...props} typeOfPage={TYPE_OF_PAGE_DESTINATION} />} />
      }

      {config.deletedPages.map(deleted =>
        <Route key={deleted} path={buildPath(match, deleted)}>
          <HttpStatus code={410}>
            <ErrorPage error={{ status: 410, message: 'The requested page is no longer available' }} />
          </HttpStatus>
        </Route>,
      )}

      {/* Destination Alert Pages */}
      {config.DESTINATION_ALERTS_ENABLED && <Route path={buildPath(match, '/destination/popular/:tag')} component={PopularDestinationPage} />}
      {config.DESTINATION_ALERTS_ENABLED && <Route path={buildPath(match, '/destination/:level/:location/:placeId')} component={DestinationPage} />}

      {/* New Destination Pages for Maldives, Greece and Bali */}
      <Route path={buildPath(match, '/destination-preview/:destination')} component={NewDestinationLandingPage} />
      <Route path={buildPath(match, '/destination/:destination')} component={NewDestinationLandingPage} />
      <RedirectWithStatus code={301} from={buildPath(match, '/hotels/country/maldives')} to={buildPath(match, '/destination/maldives')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/hotels/country/bali')} to={buildPath(match, '/destination/bali')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/hotels/country/greece')} to={buildPath(match, '/destination/greece')} />
      <RedirectWithStatus code={301} from={buildPath(match, '/hotels/country/spain')} to={buildPath(match, '/destination/spain')} />

      {/* New URL Structure For SEO Destination/Product pages */}
      <Route path={buildPath(match, '/:productType/country/:country')} component={DestinationProductPage} />
      <Route path={buildPath(match, '/:productType/city/:city/:state/:country')} component={DestinationProductPage} />
      <Route path={buildPath(match, '/:productType/city/:city/:country')} component={DestinationProductPage} />
      <Route path={buildPath(match, '/:productType/location/:location/:city/:state/:country')} component={DestinationProductPage} />
      <Route path={buildPath(match, '/:productType/location/:location/:city/:country')} component={DestinationProductPage} />
      <Route path={buildPath(match, '/:productType/location/:location/:country')} component={DestinationProductPage} />

      {/* SEO Destination/Product pages */}
      <Route path={buildPath(match, '/:location.:holidayType.:productType')} component={DestinationProductPage} />
      <Route path={buildPath(match, '/:locationOrHolidayType.:productType')} component={DestinationProductPage} />

      {/* PODS */}
      <Route path={buildPath(match, '/podspodspods/:destinationId')} component={DestinationGuidePage} />

      {config.LERE_ENABLE_TOP_PICKS_FOR_ME && <Route path={buildPath(match, '/top-picks-for-me')} component={TopPicksForMePage} />}

      <AuthRoute
        showPopup
        path={buildPath(match, '/ancillary-payment/:orderId/:type(deposit-balance|instalment-balance|instalment|reserve-balance)')}
        component={AncillaryPaymentPage}
      />
      <AuthRoute
        showPopup
        path={buildPath(match, '/checkout-deep-link')}
        component={CheckoutDeepLinkPage}
      />
      <AuthRoute
        path={buildPath(match, '/checkout-gateway')}
        component={CheckoutGatewayPage}
      />
      <Route path={buildPath(match, '/checkout/:step?')}>
        <CheckoutPageRoutes match={match} />
      </Route>

      <Route path={buildPath(match, '/trip-planner')}>
        <TripPlannerRoutes match={match} />
      </Route>

      <Route path={buildPath(match, '/splashpage')} component={SplashPage}/>
      <Route exact path={buildPath(match, '/hotels/:holidayType')} component={HolidayTypeLandingPage} />
      <Route exact path={buildPath(match, '/hotels/:holidayType/:country')} component={HolidayTypeLandingPage} />
      <Route exact path={buildPath(match, '/hotels/:holidayType/:country/:cityOrState')} component={HolidayTypeLandingPage} />

      {config.HOME_PAGE_PREVIEW_ENABLED && <Route path={buildPath(match, '/home-preview')} component={NewHomePage} /> }

      {config.businessTraveller.isEnabled && <BusinessTravellerPageRoutes match={match} />}

      <Route>
        <HttpStatus code={404}>
          <Error404Page />
        </HttpStatus>
      </Route>
    </Switch>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: isLoggedIn(state),
    currentRegionCode: state.geo.currentRegionCode,
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state) || isFlightsCredit(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
}

export default withRouter(connect(mapStateToProps)(PageRoutes))
